import { ArrowBackRounded, ErrorOutlineRounded, HistoryRounded } from "@mui/icons-material";
import {
  Box,
  Paper,
  Skeleton,
  Stack,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { observer } from "mobx-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  normalizeSwayyApiError,
  SwayyApiError,
} from "../../../api/SwayyProductsAPI";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import { userStore } from "../../../../../stores/sharedStores";
import { forceLogout } from "../../../../../api/AuthSession";
import { SwayyEmployeeRoles } from "../../../../../models/Employees/SwayyEmployeeRoles";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { SwayyProductDetailTab } from "../../../models/products/SwayyProductDetail";
import { SwayyProductStatus } from "../../../models/products/SwayyProduct";
import { SwayyProductDetailStore } from "../../../stores/SwayyProductDetailStore";
import { SwayyProductActivityLogsStore } from "../../../stores/SwayyProductActivityLogsStore";
import { SwayyProductImagesStore } from "../../../stores/SwayyProductImagesStore";
import { SwayyProductVariantsStore } from "../../../stores/SwayyProductVariantsStore";
import { swayyColors, swayyRadii } from "../../../theme/tokens";
import InlineCopyButton from "../../components/common/InlineCopyButton";
import SwayyProductImagesPanel from "../../components/products/SwayyProductImagesPanel";
import SwayyProductInfoForm from "../../components/products/SwayyProductInfoForm";
import SwayyProductSeoPanel from "../../components/products/SwayyProductSeoPanel";
import SwayyProductTabs from "../../components/products/SwayyProductTabs";
import SwayyProductVariantsPanel from "../../components/products/SwayyProductVariantsPanel";
import SwayyProductActivityLogsDrawer from "../../components/products/SwayyProductActivityLogsDrawer";
import { SwayyPageHeader } from "../../components/SwayyPageShell";

const TAB_IDS: SwayyProductDetailTab[] = [
  "info",
  "variants",
  "images",
  "seo",
];

const statusStyles: Record<
  SwayyProductStatus,
  { background: string; color: string }
> = {
  draft: {
    background: alpha(swayyColors.warning, 0.1),
    color: swayyColors.warning,
  },
  live: {
    background: alpha(swayyColors.success, 0.1),
    color: swayyColors.success,
  },
  archived: {
    background: swayyColors.pageBg,
    color: swayyColors.textSecondary,
  },
};

const parseTab = (value: string | null): SwayyProductDetailTab =>
  value && TAB_IDS.includes(value as SwayyProductDetailTab)
    ? (value as SwayyProductDetailTab)
    : "info";

const StatusBadge = ({ status }: { status: SwayyProductStatus }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      minHeight: 24,
      px: 1.2,
      borderRadius: swayyRadii.full,
      backgroundColor: statusStyles[status].background,
      color: statusStyles[status].color,
      fontSize: 11,
      fontWeight: 700,
      textTransform: "capitalize",
    }}
  >
    {status}
  </Box>
);
const detailSurfaceSx = {
  borderRadius: swayyRadii.lg,
  borderColor: swayyColors.border,
  backgroundColor: swayyColors.white,
} as const;

const DetailState = ({
  title,
  description,
  onBack,
  onRetry,
}: {
  title: string;
  description: string;
  onBack: () => void;
  onRetry?: () => void;
}) => (
  <Stack
    alignItems="center"
    justifyContent="center"
    textAlign="center"
    sx={{ px: 2, py: { xs: 8, md: 10 }, minHeight: 360 }}
  >
    <Box
      sx={{
        width: 72,
        height: 72,
        display: "grid",
        placeItems: "center",
        borderRadius: swayyRadii.full,
        backgroundColor: swayyColors.pineLight,
        color: swayyColors.danger,
        mb: 2,
        "& .MuiSvgIcon-root": { fontSize: 34 },
      }}
    >
      <ErrorOutlineRounded />
    </Box>
    <CustomTypography
      variant="heading4"
      color={swayyColors.deepGreen}
      sx={{ mb: 1 }}
    >
      {title}
    </CustomTypography>
    <CustomTypography
      variant="body1"
      color={swayyColors.textSecondary}
      sx={{ maxWidth: 420, mb: 3 }}
    >
      {description}
    </CustomTypography>
    <Stack direction="row" spacing={1.5}>
      <AppButton
        label="Back to products"
        onClick={onBack}
        customStyles={{
          color: swayyColors.deepGreen,
          border: `1px solid ${swayyColors.border}`,
          borderRadius: swayyRadii.md,
          "&:hover": { backgroundColor: swayyColors.pineLight },
        }}
      />
      {onRetry && (
        <AppButton
          label="Try again"
          onClick={onRetry}
          customStyles={{
            color: swayyColors.white,
            backgroundColor: swayyColors.pine,
            borderRadius: swayyRadii.md,
            "&:hover": { backgroundColor: swayyColors.pineHover },
          }}
        />
      )}
    </Stack>
  </Stack>
);

const SwayyProductDetail = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotification();
  const showErrorRef = useRef(showError);
  showErrorRef.current = showError;
  const storeRef = useRef<SwayyProductDetailStore | null>(null);
  const variantsStoreRef = useRef<SwayyProductVariantsStore | null>(null);
  const imagesStoreRef = useRef<SwayyProductImagesStore | null>(null);
  const activityLogsStoreRef = useRef<SwayyProductActivityLogsStore | null>(
    null,
  );
  const variantsLoadedForProductRef = useRef<number | null>(null);
  const imagesLoadedForProductRef = useRef<number | null>(null);
  const handlingUnauthorized = useRef(false);
  const listingSearch =
    (location.state as { fromSearch?: string } | null)?.fromSearch || "";
  const isCreateRoute = id === "new";
  const canViewCatalog = userStore.hasSwayyPermission([
    SwayyEmployeeRoles.VIEW_CATALOG,
    SwayyEmployeeRoles.EDIT_CATALOG,
  ]);
  const canEditCatalog = userStore.hasSwayyPermission(
    SwayyEmployeeRoles.EDIT_CATALOG,
  );

  if (!storeRef.current) {
    storeRef.current = new SwayyProductDetailStore();
  }
  if (!variantsStoreRef.current) {
    variantsStoreRef.current = new SwayyProductVariantsStore();
  }
  if (!imagesStoreRef.current) {
    imagesStoreRef.current = new SwayyProductImagesStore();
  }
  if (!activityLogsStoreRef.current) {
    activityLogsStoreRef.current = new SwayyProductActivityLogsStore();
  }
  const store = storeRef.current;
  const variantsStore = variantsStoreRef.current;
  const imagesStore = imagesStoreRef.current;
  const activityLogsStore = activityLogsStoreRef.current;

  const productId = Number(id);
  const [activeTab, setActiveTab] = useState<SwayyProductDetailTab>(() =>
    parseTab(new URLSearchParams(location.search).get("tab")),
  );
  const [activityLogsOpen, setActivityLogsOpen] = useState(false);

  const goToListing = useCallback(() => {
    navigate({
      pathname: "/products",
      search: listingSearch.replace(/^\?/, ""),
    });
  }, [listingSearch, navigate]);

  const handleUnauthorized = useCallback(() => {
    if (handlingUnauthorized.current) return;
    handlingUnauthorized.current = true;
    void forceLogout();
  }, []);

  const handleApiError = useCallback(
    (error: SwayyApiError, message: string) => {
      if (error.status === 401) {
        handleUnauthorized();
        return;
      }
      showErrorRef.current(
        "Error",
        error.status === 403
          ? "You don’t have permission to view this product."
          : message,
      );
    },
    [handleUnauthorized],
  );

  const loadDetail = useCallback(async () => {
    if (!canViewCatalog) return;
    if (isCreateRoute) {
      if (!canEditCatalog) return;
      store.prepareForCreate();
      return;
    }
    if (!Number.isInteger(productId) || productId <= 0) {
      store.markNotFound();
      return;
    }
    // Hydrated from POST create response — avoid a redundant GET.
    if (store.product?.id === productId && store.loadState === "ready") {
      return;
    }
    const error = await store.loadProduct(productId);
    if (error?.status === 401) {
      handleUnauthorized();
      return;
    }
    if (error) handleApiError(error, "Failed to load product details.");
  }, [
    canEditCatalog,
    canViewCatalog,
    handleApiError,
    handleUnauthorized,
    isCreateRoute,
    productId,
    store,
  ]);

  useEffect(() => {
    if (!canViewCatalog) return;
    void loadDetail();
    void store.loadFilterOptions().then((error) => {
      if (error) handleApiError(error, "Failed to load form options.");
    });
    return () => store.dispose();
  }, [canViewCatalog, handleApiError, loadDetail, store]);

  useEffect(() => {
    return () => {
      variantsStore.dispose();
      imagesStore.dispose();
      activityLogsStore.reset();
    };
  }, [activityLogsStore, imagesStore, variantsStore]);

  useEffect(() => {
    variantsLoadedForProductRef.current = null;
    imagesLoadedForProductRef.current = null;
  }, [productId]);

  useEffect(() => {
    if (!canViewCatalog) return;
    if (isCreateRoute) return;
    if (activeTab !== "variants") return;
    if (store.loadState !== "ready") return;
    if (!Number.isInteger(productId) || productId <= 0) return;
    if (variantsLoadedForProductRef.current === productId) return;

    variantsLoadedForProductRef.current = productId;
    void variantsStore.loadVariantsTab(productId).then((error) => {
      if (error) {
        variantsLoadedForProductRef.current = null;
        handleApiError(error, "Failed to load variants.");
      }
    });
  }, [
    activeTab,
    canViewCatalog,
    handleApiError,
    isCreateRoute,
    productId,
    store.loadState,
    variantsStore,
  ]);

  useEffect(() => {
    if (!canViewCatalog) return;
    if (isCreateRoute) return;
    if (activeTab !== "images") return;
    if (store.loadState !== "ready") return;
    if (!Number.isInteger(productId) || productId <= 0) return;
    if (imagesLoadedForProductRef.current === productId) return;

    imagesLoadedForProductRef.current = productId;
    void imagesStore.loadImagesTab(productId).then((error) => {
      if (error) {
        imagesLoadedForProductRef.current = null;
        handleApiError(error, "Failed to load images.");
      }
    });
  }, [
    activeTab,
    canViewCatalog,
    handleApiError,
    imagesStore,
    isCreateRoute,
    productId,
    store.loadState,
  ]);

  useEffect(() => {
    if (isCreateRoute) {
      setActiveTab("info");
      return;
    }
    const params = new URLSearchParams(location.search);
    const currentTab = params.get("tab");
    if (activeTab === "info") {
      if (currentTab) {
        params.delete("tab");
        navigate(
          { search: params.toString() ? `?${params.toString()}` : "" },
          { replace: true, state: location.state },
        );
      }
      return;
    }
    if (currentTab !== activeTab) {
      params.set("tab", activeTab);
      navigate(
        { search: `?${params.toString()}` },
        { replace: true, state: location.state },
      );
    }
  }, [
    activeTab,
    isCreateRoute,
    location.search,
    location.state,
    navigate,
  ]);

  const handleTabChange = (tab: SwayyProductDetailTab) => {
    if (isCreateRoute && tab !== "info") return;
    setActiveTab(tab);
  };

  const handleSave = async () => {
    try {
      if (isCreateRoute || store.isCreateMode) {
        const result = await store.createProduct();
        if ("error" in result) {
          if (!store.hasFieldErrors) {
            handleApiError(result.error, "Failed to create product.");
          }
          return;
        }
        showSuccess("Draft product created.");
        navigate(`/products/${result.productId}`, {
          replace: true,
          state: { fromSearch: listingSearch },
        });
        return;
      }

      const shouldRefreshVariants = store.hasCategoryChanges;
      const error = await store.saveProduct();
      if (error) {
        if (!store.hasFieldErrors) {
          handleApiError(error, "Failed to save product.");
        }
        return;
      }
      showSuccess("Product updated.");
      if (shouldRefreshVariants) {
        variantsLoadedForProductRef.current = null;
        variantsStore.invalidateTabCache();
        if (
          activeTab === "variants" &&
          Number.isInteger(productId) &&
          productId > 0
        ) {
          variantsLoadedForProductRef.current = productId;
          void variantsStore
            .loadVariantsTab(productId, { force: true })
            .then((loadError) => {
              if (loadError) {
                variantsLoadedForProductRef.current = null;
                handleApiError(loadError, "Failed to load variants.");
              }
            });
        }
      }
    } catch (error) {
      const apiError = normalizeSwayyApiError(
        error,
        isCreateRoute
          ? "Unable to create product."
          : "Unable to save product changes.",
      );
      if (!store.hasFieldErrors) {
        handleApiError(
          apiError,
          isCreateRoute ? "Failed to create product." : "Failed to save product.",
        );
      }
    }
  };

  const handleCancel = () => {
    if (!isCreateRoute && store.hasChanges) {
      store.discardChanges();
    }
    goToListing();
  };

  const isLoading =
    !isCreateRoute &&
    (store.loadState === "idle" || store.loadState === "loading");
  const formDisabled = store.isSaving || isLoading || !canEditCatalog;
  const formMode = isCreateRoute || store.isCreateMode ? "create" : "edit";

  if (!canViewCatalog || (isCreateRoute && !canEditCatalog)) {
    return <PermissionBlockLabel />;
  }

  return (
    <Box sx={{ p: { xs: 0.5, md: 1 }, minWidth: 0 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", sm: "flex-start" }}
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Box>
          <AppButton
            label="Back"
            startIcon={<ArrowBackRounded />}
            onClick={goToListing}
            customStyles={{
              mb: 1.5,
              color: swayyColors.deepGreen,
              border: `1px solid ${swayyColors.border}`,
              borderRadius: swayyRadii.md,
              "&:hover": { backgroundColor: swayyColors.pineLight },
            }}
          />
          <SwayyPageHeader
            title={
              formMode === "create"
                ? "Add Product"
                : store.product?.name || "Product detail"
            }
            description={
              formMode === "create"
                ? "Create a draft product with basic information"
                : "Review and update basic product information"
            }
          />
        </Box>
        {formMode === "create" ? (
          <StatusBadge status="draft" />
        ) : (
          store.product && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
            >
              <AppButton
                label="Activity Logs"
                startIcon={<HistoryRounded />}
                onClick={() => setActivityLogsOpen(true)}
                customStyles={{
                  color: swayyColors.deepGreen,
                  border: `1px solid ${swayyColors.border}`,
                  borderRadius: swayyRadii.md,
                  "&:hover": { backgroundColor: swayyColors.pineLight },
                }}
              />
              <StatusBadge status={store.product.status} />
              <Stack direction="row" spacing={0.5} alignItems="center">
                <CustomTypography
                  variant="body2"
                  color={swayyColors.textSecondary}
                >
                  SKU {store.product.sku}
                </CustomTypography>
                {store.product.sku ? (
                  <InlineCopyButton
                    value={store.product.sku}
                    title="Copy SKU"
                    ariaLabel="Copy product SKU"
                  />
                ) : null}
              </Stack>
            </Stack>
          )
        )}
      </Stack>

      {isLoading ? (
        <Paper
          elevation={0}
          variant="outlined"
          sx={{ ...detailSurfaceSx, p: { xs: 2, md: 2.5 } }}
        >
          <Stack spacing={2}>
            <Skeleton height={44} />
            <Skeleton height={40} />
            <Skeleton height={120} />
            <Skeleton height={40} />
          </Stack>
        </Paper>
      ) : store.loadState === "notFound" ? (
        <Paper elevation={0} variant="outlined" sx={detailSurfaceSx}>
          <DetailState
            title="Product not found"
            description={store.errorMessage}
            onBack={goToListing}
          />
        </Paper>
      ) : store.loadState === "forbidden" ? (
        <Paper elevation={0} variant="outlined" sx={detailSurfaceSx}>
          <DetailState
            title="Permission denied"
            description={store.errorMessage}
            onBack={goToListing}
          />
        </Paper>
      ) : store.loadState === "error" ? (
        <Paper elevation={0} variant="outlined" sx={detailSurfaceSx}>
          <DetailState
            title="Unable to load product"
            description={store.errorMessage}
            onBack={goToListing}
            onRetry={() => void loadDetail()}
          />
        </Paper>
      ) : (
        <>
          {formMode === "edit" && (
            <Paper
              elevation={0}
              variant="outlined"
              sx={{
                ...detailSurfaceSx,
                px: { xs: 1.5, md: 2 },
                pt: 0.5,
                mb: 2,
              }}
            >
              <SwayyProductTabs
                activeTab={activeTab}
                onChange={handleTabChange}
              />
            </Paper>
          )}

          {(formMode === "create" || activeTab === "info") && (
            <SwayyProductInfoForm
              store={store}
              mode={formMode}
              disabled={formDisabled}
              onCancel={handleCancel}
              onSave={() => void handleSave()}
            />
          )}

          {formMode === "edit" && activeTab === "variants" && (
            <SwayyProductVariantsPanel
              productId={productId}
              store={variantsStore}
              disabled={!canEditCatalog}
              onApiError={handleApiError}
              onSuccess={(_title, message) => showSuccess(message)}
            />
          )}
          {formMode === "edit" && activeTab === "images" && (
            <SwayyProductImagesPanel
              productId={productId}
              store={imagesStore}
              disabled={!canEditCatalog}
              onApiError={handleApiError}
              onSuccess={(message) => showSuccess(message)}
            />
          )}
          {formMode === "edit" && activeTab === "seo" && (
            <SwayyProductSeoPanel
              store={store}
              disabled={formDisabled}
              onCancel={handleCancel}
              onSave={() => void handleSave()}
            />
          )}
        </>
      )}

      <SwayyProductActivityLogsDrawer
        open={activityLogsOpen}
        onClose={() => setActivityLogsOpen(false)}
        productId={formMode === "edit" ? productId : null}
        store={activityLogsStore}
      />
    </Box>
  );
};

export default observer(SwayyProductDetail);
